import { matchdayApi, teamApi } from '@/api/endpoints';
import type { BonusOverview, BonusOverviewEntry, Matchday, Question, Team } from '@/api/types';
import { Alert, Card, Spinner } from '@/components/ui';
import { useAuth } from '@/auth/AuthContext';
import { formatDateTime, isPastDeadline } from '@/lib/datetime';
import { truncateForOverview } from '@/lib/text';
import { answerLabel, questionHeaderLabel, solutionLabel } from '@/features/questions/questionLabels';
import { useAsync } from '@/lib/useAsync';
import adminStyles from '@/features/admin/admin.module.css';
import ergStyles from '@/features/ergebnistipp/ergebnistipp.module.css';
import styles from './bonus.module.css';

/** Zeilenhintergrund des angemeldeten Nutzers (analog Ergebnistipp-Tippübersicht). */
const SELF_ROW_BACKGROUND = 'rgba(10, 122, 58, 0.05)';

interface LoadedData {
  overview: BonusOverview;
  teams: Team[];
}

/**
 * Bonus-Tippübersicht — als Raster wie die Ergebnistipp-Tippübersicht (Mitspieler ×
 * Fragen, Kürzel in der Kopfzeile via `questionHeaderLabel`). Vor Tippschluss nur
 * „wer hat getippt" (Sichtbarkeitsregel §13.0, eigene Antworten sichtbar, fremde
 * verdeckt); danach volle Antworten + Lösung-Zeile + Punkte je Frage.
 */
export function BonusOverviewPage({
  matchday,
  showHeader = true,
}: {
  matchday: Matchday;
  /** false, wenn die Tippübersicht die Überschrift bereits selbst rendert (Reiter-Wechsel). */
  showHeader?: boolean;
}) {
  const matchdayId = matchday.id;
  const { user } = useAuth();

  const { data, loading, error } = useAsync<LoadedData>(
    async (signal) => {
      const [overview, teams] = await Promise.all([
        matchdayApi.questionsOverview(matchdayId, signal),
        teamApi.list(signal).catch(() => [] as Team[]),
      ]);
      return { overview, teams };
    },
    [matchdayId],
    { refreshOnFocus: true },
  );

  if (loading) return <Spinner label="Übersicht wird geladen…" />;
  if (error) return <Alert>{error}</Alert>;
  if (!data) return <Alert>Keine Daten.</Alert>;

  const { overview, teams } = data;
  const md = overview.matchday ?? matchday;
  const questions = [...overview.questions].sort((a, b) => a.sort_order - b.sort_order);
  const entries = overview.overview;
  const deadlinePassed = overview.deadline_passed ?? isPastDeadline(md.deadline_at);

  const teamName = (id: number | null | undefined): string => {
    if (id == null) return '—';
    const t = teams.find((x) => x.id === id);
    return t?.short_name ?? t?.name ?? `#${id}`;
  };

  return (
    <>
      {showHeader && <h1>Tippübersicht — {md.name}</h1>}
      <p className="muted">Tippschluss: {formatDateTime(md.deadline_at)}</p>

      {!deadlinePassed ? (
        <PendingTable entries={entries} questions={questions} selfId={user?.id} teamName={teamName} />
      ) : (
        <>
          <Card>
            <div className={styles.solutionRow}>
              <strong>Lösung:</strong>
              {questions.map((q) => (
                <span key={q.id}>
                  {q.prompt}: {solutionLabel(q, teamName)}
                </span>
              ))}
            </div>
          </Card>
          <ResultGrid entries={entries} questions={questions} selfId={user?.id} teamName={teamName} />
        </>
      )}
    </>
  );
}

/** Punkte eines Mitspielers über alle Fragen dieses Bonus-Spieltags. */
function sumPoints(entry: BonusOverviewEntry): number {
  if (!entry.answers) return 0;
  return entry.answers.reduce((acc, a) => acc + (a.points ?? 0), 0);
}

/** Tabelle vor Tippschluss: alle Teilnehmer, eigene Antworten sichtbar, fremde verdeckt (§13.0). */
function PendingTable({
  entries,
  questions,
  selfId,
  teamName,
}: {
  entries: BonusOverviewEntry[];
  questions: Question[];
  selfId: number | undefined;
  teamName: (id: number | null | undefined) => string;
}) {
  if (entries.length === 0) {
    return (
      <Card>
        <p className="muted">Noch keine Mitspieler vorhanden.</p>
      </Card>
    );
  }

  const rows = [...entries].sort((a, b) => a.display_name.localeCompare(b.display_name));

  return (
    <Card>
      <div style={{ overflowX: 'auto' }}>
        <table className={`${adminStyles.rankTable} ${ergStyles.overviewTable} ${ergStyles.noRankLeader}`}>
          <thead>
            <tr>
              <th className={adminStyles.rankPos}>#</th>
              <th className={ergStyles.nameCol}>Name</th>
              {questions.map((q, i) => (
                <th key={q.id} className={ergStyles.tipCol} title={q.prompt}>
                  {questionHeaderLabel(q, i)}
                </th>
              ))}
              <th className={ergStyles.numCol}>B</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((entry) => {
              const isSelf = entry.user_id === selfId;
              const hidden = !isSelf && entry.has_tipped;
              const own = isSelf ? entry.answers : null;
              const byQuestion = new Map((own ?? []).map((a) => [a.question_id, a] as const));
              const selfRowStyle = isSelf ? { background: SELF_ROW_BACKGROUND } : undefined;
              return (
                <tr key={entry.user_id}>
                  <td className={adminStyles.rankPos} style={selfRowStyle}>
                    {entry.standings?.rank ?? '—'}
                  </td>
                  <td
                    className={`${ergStyles.nameCol} ${isSelf ? ergStyles.overviewSelf : ''}`}
                    style={selfRowStyle}
                  >
                    {truncateForOverview(entry.display_name)}
                  </td>
                  {questions.map((q) => {
                    const a = byQuestion.get(q.id);
                    return (
                      <td key={q.id} className={ergStyles.tipCol} style={selfRowStyle}>
                        {a ? answerLabel(q, a, teamName) : hidden ? '—' : ''}
                      </td>
                    );
                  })}
                  {/* Vor Tippschluss gibt es noch keine Bonuspunkte — "—" statt einer
                      irreführenden 0 (analog Ergebnistipp-Tippübersicht). */}
                  <td className={ergStyles.numCol} style={selfRowStyle}>
                    —
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}

/**
 * Tabelle nach Tippschluss: Antworten + Punkte je Frage. Sortiert nach
 * Gesamtpunkten (wie Gesamtübersicht/Ergebnistipp-Tippübersicht), Rang links.
 */
function ResultGrid({
  entries,
  questions,
  selfId,
  teamName,
}: {
  entries: BonusOverviewEntry[];
  questions: Question[];
  selfId: number | undefined;
  teamName: (id: number | null | undefined) => string;
}) {
  const rows = entries
    .map((e) => ({ entry: e, p: sumPoints(e) }))
    .sort(
      (a, b) =>
        (b.entry.standings?.total ?? 0) - (a.entry.standings?.total ?? 0) ||
        a.entry.display_name.localeCompare(b.entry.display_name),
    );

  return (
    <Card>
      <div style={{ overflowX: 'auto' }}>
        <table className={`${adminStyles.rankTable} ${ergStyles.overviewTable}`}>
          <thead>
            <tr>
              <th className={adminStyles.rankPos}>#</th>
              <th className={ergStyles.nameCol}>Name</th>
              {questions.map((q, i) => (
                <th key={q.id} className={ergStyles.tipCol} title={q.prompt}>
                  {questionHeaderLabel(q, i)}
                </th>
              ))}
              <th className={ergStyles.numCol}>B</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ entry, p }) => {
              const isSelf = entry.user_id === selfId;
              const selfRowStyle = isSelf ? { background: SELF_ROW_BACKGROUND } : undefined;
              const byQuestion = new Map((entry.answers ?? []).map((a) => [a.question_id, a] as const));
              return (
                <tr key={entry.user_id}>
                  <td className={adminStyles.rankPos} style={selfRowStyle}>
                    {entry.standings?.rank ?? '—'}
                  </td>
                  <td
                    className={`${ergStyles.nameCol} ${isSelf ? ergStyles.overviewSelf : ''}`}
                    style={selfRowStyle}
                  >
                    {truncateForOverview(entry.display_name)}
                  </td>
                  {questions.map((q) => {
                    const a = byQuestion.get(q.id);
                    return (
                      <td key={q.id} className={ergStyles.tipCol} style={selfRowStyle}>
                        {a ? (
                          <span>
                            {answerLabel(q, a, teamName)}
                            {a.points != null && (
                              <sup className={styles.tipPoints}>{a.points}</sup>
                            )}
                          </span>
                        ) : (
                          '—'
                        )}
                      </td>
                    );
                  })}
                  <td className={ergStyles.numCol} style={selfRowStyle}>
                    {p}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
